/**
 * @fileoverview Section "Logos" — assets brand Quipux servis depuis `public/brand/`.
 *
 * Trois variantes livrées :
 *   - **Logo couleur** (fond clair) — usage par défaut, wordmark navy + halftone Afrique.
 *   - **Logo blanc** (fond sombre) — sidebar navy, PDF sombre, réseaux sociaux fond photo.
 *   - **Favicon** — icône navigateur, PWA, raccourci d'écran.
 *
 * La section expose aussi les logos des opérateurs mobile money CI via
 * `<OperatorLogo>` (assets `public/operators/<CODE>.svg`, codes OpenAPI).
 *
 * Convention : ne PAS recouper ou changer les couleurs du logo dans le code
 * (SVG inline manipulable → risque de dérive). Toujours utiliser les
 * fichiers versionnés dans `apps/*&#47;public/brand/`.
 */

import { CopyButton, OperatorLogo } from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

interface LogoAssetProps {
  src: string;
  alt: string;
  path: string;
  usage: string;
  tone: 'default' | 'dark';
  maxHeight: number;
}

function LogoAsset({ src, alt, path, usage, tone, maxHeight }: LogoAssetProps): JSX.Element {
  return (
    <div className="space-y-2">
      <DsPreview tone={tone} align="center" className="min-h-40">
        <img src={src} alt={alt} style={{ maxHeight: `${maxHeight}px` }} className="w-auto" />
      </DsPreview>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-quipux-navy-950">{alt}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{usage}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <code className="rounded bg-muted px-2 py-0.5 font-mono text-[10px] text-quipux-navy-900">
            {path}
          </code>
          <CopyButton value={path} ariaLabel={`Copier le chemin ${path}`} />
        </div>
      </div>
    </div>
  );
}

export function DsSectionLogos(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="grid gap-6 lg:grid-cols-2">
        <LogoAsset
          src={`${import.meta.env.BASE_URL}brand/logo_quipux.png`}
          alt="Quipux Afrique — logo couleur"
          path="/brand/logo_quipux.png"
          usage="Fond clair par défaut : en-tête portail, factures PDF, e-mails."
          tone="default"
          maxHeight={120}
        />
        <LogoAsset
          src={`${import.meta.env.BASE_URL}brand/logo_quipux_blanc.png`}
          alt="Quipux Afrique — logo blanc"
          path="/brand/logo_quipux_blanc.png"
          usage="Fond sombre : sidebar console navy, réseaux sociaux, PDF sombre."
          tone="dark"
          maxHeight={120}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <LogoAsset
          src={`${import.meta.env.BASE_URL}brand/favicon.ico`}
          alt="Favicon Quipux"
          path="/favicon.ico"
          usage="Icône onglet navigateur, PWA, raccourci d'écran."
          tone="default"
          maxHeight={64}
        />
        <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          <p className="mb-2 font-medium text-quipux-navy-900">À produire (backlog)</p>
          <ul className="list-inside list-disc space-y-1 text-xs">
            <li>Version SVG des logos (actuellement PNG uniquement)</li>
            <li>
              Variantes favicon : <code>apple-touch-icon.png</code>,{' '}
              <code>manifest.webmanifest</code>, tailles 16/32/180/512
            </li>
            <li>Open Graph / Twitter Card (partage social portail)</li>
          </ul>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          Logos opérateurs mobile money
        </h3>
        <DsPreview label="OperatorLogo — codes OpenAPI + fallback code inconnu">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <OperatorLogo code="OM_CI" withLabel />
            <OperatorLogo code="MTN_CI" withLabel />
            <OperatorLogo code="MOOV_CI" withLabel />
            <OperatorLogo code="WAVE_CI" withLabel />
            <OperatorLogo code={null} withLabel />
          </div>
        </DsPreview>
        <p className="text-muted-foreground text-xs leading-5">
          Les codes (<code>OM_CI</code>, <code>MTN_CI</code>, <code>MOOV_CI</code>,{' '}
          <code>WAVE_CI</code>) suivent l'enum OpenAPI <code>OperatorCode</code> — le composant
          mappe chaque code vers son asset <code>/operators/&lt;CODE&gt;.svg</code> et son libellé
          FR centralisé. <code>MOOV_CI</code> est servi par le logo Flooz, la marque mobile money
          de Moov Africa en Côte d'Ivoire. Un code <code>null</code> (usager pas encore passé au
          guichet) affiche le fallback neutre « Non renseigné », jamais une erreur.
        </p>
      </div>

      <div className="rounded-lg border border-quipux-navy-200 bg-quipux-navy-50 p-4 text-sm text-quipux-navy-900">
        <p className="mb-2 font-semibold">Règles d'utilisation</p>
        <ul className="list-inside list-disc space-y-1 text-xs">
          <li>Espace de garde autour du logo ≥ hauteur du "Q" du wordmark.</li>
          <li>
            Ne pas déformer, recolorer, ombrer, incliner ou ajouter d'effet. Le logo est
            versionné dans <code>Identite visuelle/brand/</code> comme source unique.
          </li>
          <li>
            Sur fond photo : privilégier la variante blanche + assombrissement de la
            photo si nécessaire pour maintenir un contraste suffisant.
          </li>
        </ul>
      </div>
    </div>
  );
}
